import type { GameState } from '../game/state';
import type { InputManager } from '../input/input-manager';
import {
  t,
  ResourceTier,
  RESOURCE_INFO,
  EntityType,
  getZoneDifficulty,
  sectorName,
  sectorKey,
  STATION_SAFE_RADIUS,
  WARP_GATE_SAFE_RADIUS,
  SECTOR_SIZE,
  lookupWeapon,
  lookupShield,
  RACE_INFO,
  raceNameKey,
  shipNameKey,
  type RaceId,
  type ShipClass,
  type Vec3,
  type SectorClaim,
} from '@viral-space/shared';

const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  danger: '#ff4444',
  bg: 'rgba(0,16,0,0.6)',
  bgSolid: 'rgba(0,16,0,0.85)',
  dim: 'rgba(0,255,65,0.3)',
  hostile: '#ff4444',
  friendly: '#00ff41',
  neutral: '#ffb000',
  object: '#ffffff',
  station: '#00ddff',
  warpGate: '#bb77ff',
} as const satisfies Record<string, string>;

const FONT = "'Share Tech Mono', monospace";

const ENTITY_TYPE_NAMES: Partial<Record<EntityType, string>> = {
  [EntityType.Ship]: 'SHIP',
  [EntityType.NPC]: 'NPC',
  [EntityType.Asteroid]: 'ASTEROID',
  [EntityType.Planet]: 'PLANET',
  [EntityType.Star]: 'STAR',
  [EntityType.Station]: 'STATION',
  [EntityType.WarpGate]: 'WARP GATE',
  [EntityType.Comet]: 'COMET',
  [EntityType.ResourceDrop]: 'RESOURCE',
  [EntityType.Projectile]: 'PROJECTILE',
};

const GLOW = `0 0 6px ${COLORS.primary}, 0 0 12px rgba(0,255,65,0.3)`;
const GLOW_DANGER = `0 0 6px ${COLORS.danger}, 0 0 12px rgba(255,68,68,0.3)`;
const GLOW_WARN = `0 0 6px ${COLORS.secondary}`;
const BOX_GLOW = `0 0 8px rgba(0,255,65,0.4), inset 0 0 8px rgba(0,255,65,0.05)`;

/**
 * Liftoff is gated to physical proximity to a Spaceport
 * (`extra.surfaceBase === true` + `extra.kind === BaseKind.Spaceport`,
 * which serializes to `kind === 3`). The launchpad IS the pickup
 * point — flying to the perimeter of the map and hitting LIFTOFF
 * would feel like cheating, and PvP rules require a recognisable
 * ground anchor for the transition so hostile players can intercept
 * a fleeing target before they make it back to orbit.
 */
const SPACEPORT_LIFTOFF_RADIUS = 60;
const SPACEPORT_KIND = 3;

type LandTargetEntity = {
  id: string;
  type: EntityType;
  transform: { position: { x: number; y: number; z: number } };
  extra?: Record<string, unknown>;
};

/**
 * Returns true iff `player` is within `radius` units (planar XZ
 * distance, ignoring altitude over terrain) of any entity flagged as
 * a Spaceport surface base. Exported as a pure helper so the LAND /
 * LIFTOFF decision can be unit-tested without standing up a real HUD
 * or DOM.
 */
export function isNearSpaceport(
  player: { transform: { position: { x: number; y: number; z: number } } } | null,
  entities: Iterable<LandTargetEntity>,
  radius: number = SPACEPORT_LIFTOFF_RADIUS,
): boolean {
  if (!player) return false;
  const pp = player.transform.position;
  const radSq = radius * radius;
  for (const e of entities) {
    const x = e.extra;
    if (!x || x['surfaceBase'] !== true) continue;
    if ((x['kind'] as number | undefined) !== SPACEPORT_KIND) continue;
    const sp = e.transform.position;
    const dx = sp.x - pp.x;
    const dz = sp.z - pp.z;
    if (dx * dx + dz * dz <= radSq) return true;
  }
  return false;
}

/**
 * Returns the entity id of the closest Planet whose atmosphere edge
 * is within `landDistance` of the player, or `null` if none qualify.
 * Mirrors the in-class logic that drove the LAND prompt before this
 * refactor; exported for unit tests + reuse by the mobile LAND
 * button driver in `main.ts`.
 */
export function findClosestLandablePlanetId(
  player: { transform: { position: { x: number; y: number; z: number } } } | null,
  entities: Iterable<LandTargetEntity>,
  landDistance: number,
): string | null {
  return findClosestLandablePlanet(player, entities, landDistance)?.id ?? null;
}

/**
 * Entity-returning variant of {@link findClosestLandablePlanetId} —
 * lets callers read planet metadata (e.g. the `premiumOnly` landing
 * flag) without a second pass over the entity iterable.
 */
export function findClosestLandablePlanet(
  player: { transform: { position: { x: number; y: number; z: number } } } | null,
  entities: Iterable<LandTargetEntity>,
  landDistance: number,
): LandTargetEntity | null {
  if (!player) return null;
  const pp = player.transform.position;
  let closestDist = Infinity;
  let closest: LandTargetEntity | null = null;
  for (const e of entities) {
    if (e.type !== EntityType.Planet) continue;
    const sp = e.transform.position;
    const radius = (e.extra?.radius as number | undefined) ?? 0;
    const atmoR = (e.extra?.atmosphereRadius as number | undefined) ?? radius;
    const dx = sp.x - pp.x;
    const dy = sp.y - pp.y;
    const dz = sp.z - pp.z;
    const distToCenter = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const distToAtmo = distToCenter - atmoR;
    if (distToAtmo < landDistance && distToAtmo < closestDist) {
      closestDist = distToAtmo;
      closest = e;
    }
  }
  return closest;
}

type RadarBlipShape = 'dot' | 'square' | 'ring';

interface RadarBlip {
  x: number;
  y: number;
  color: string;
  size: number;
  opacity: number;
  shape: RadarBlipShape;
}

export class HUD {
  private container: HTMLElement;
  private root!: HTMLElement;

  private hpFill!: HTMLElement;
  private hpText!: HTMLElement;
  private shieldFill!: HTMLElement;
  private shieldText!: HTMLElement;
  private energyFill!: HTMLElement;
  private energyText!: HTMLElement;
  private energyLabel!: HTMLElement;
  private energyDefaultLabel!: string;
  /**
   * Client-side mirror of the server's boost-lockout state machine. The
   * server is authoritative (it sends `bl: 1` while gating boost), but
   * we also track this locally so the bar's red "cooling" paint reacts
   * immediately to a depleted energy bar even before the next server
   * snapshot arrives — and stays consistent when the server flag's
   * transit is delayed by jitter / dropped packets.
   *
   * State machine: armed when we observe energy at zero, cleared once
   * energy regenerates back past 20% of `maxEnergy`.
   */
  private energyCoolingLocal = false;

  private resourceEls: Map<ResourceTier, HTMLElement> = new Map();
  private creditsEl!: HTMLElement;
  private levelEl!: HTMLElement;
  private xpFill!: HTMLElement;
  private xpText!: HTMLElement;
  private statsSummaryEl!: HTMLElement;

  private radarCanvas!: HTMLCanvasElement;
  private radarCtx!: CanvasRenderingContext2D;
  private sectorCoordsEl!: HTMLElement;
  private radarCoordsEl!: HTMLElement;
  // Mobile only: second line under the radar that shows the sector
  // directly in front of the pilot's nose. On desktop this information
  // is appended to `sectorCoordsEl` on the same line, but the mobile
  // radar rail doesn't have enough horizontal room for a one-liner.
  private destSectorEl: HTMLElement | null = null;

  private weaponNameEl!: HTMLElement;
  private weaponAmmoEl!: HTMLElement;
  private weaponCooldownFill!: HTMLElement;
  private shieldNameEl!: HTMLElement;
  private shieldChargesEl!: HTMLElement;
  private fireModeEl!: HTMLElement;
  /**
   * Single-line "FIGHTER · TERRAN" badge mounted at the top of the
   * weapon/shield panel (and as a compact pill on mobile). Pulled from
   * the local `LocalPlayerState.shipClass` / `raceId`, both of which the
   * server populates immediately after join, so this element flips from
   * "—" to the real values within the first frame of gameplay.
   */
  private shipInfoEl!: HTMLElement;

  private speedEl!: HTMLElement;
  private coordsEl!: HTMLElement;
  private zoneEl!: HTMLElement;

  private targetOverlay!: HTMLElement;
  private targetNameEl!: HTMLElement;
  private targetHpFill!: HTMLElement;
  // Shield bar is built alongside the HP bar but the wrapper is hidden
  // when the target has no shield (`maxShield <= 0`), so non-shielded
  // mooks (low-level NPCs, asteroids, stations…) keep the slim two-row
  // panel they used to have.
  private targetShieldTrack!: HTMLElement;
  private targetShieldFill!: HTMLElement;
  private targetDistEl!: HTMLElement;

  private siegeBannerEl: HTMLElement | null = null;

  /**
   * Optional sink for `showNotification` events. When set (see `main.ts`,
   * where it's wired to `Chat.addActivity`), activity toasts are routed
   * into the chat transcript as a persistent activity log instead of
   * floating DOM pills on the right side of the HUD. If null the call
   * is a silent no-op — there is no longer a fallback pill renderer.
   */
  onNotify: ((text: string, colorHex: string) => void) | null = null;

  private reticle!: HTMLElement;
  private dockPrompt!: HTMLElement;
  private _nearbyStationId: string | null = null;
  private warpPrompt!: HTMLElement;
  private _nearbyWarpGateId: string | null = null;
  private tradePrompt!: HTMLElement;
  private _nearbyMerchantId: string | null = null;
  private landPrompt!: HTMLElement;
  private _nearbyPlanetId: string | null = null;
  /**
   * `true` when the closest landable planet has the `premiumOnly`
   * landing flag AND the local pilot is not Premium. Drives the gold
   * "PREMIUM" badge on the LAND prompt; `main.ts` reads it via the
   * public getter to route the land action to the upgrade modal
   * instead of sending a doomed LandRequest.
   */
  private _nearbyPlanetPremiumLocked = false;
  /** Premium snapshot pushed by main.ts (see setPremium). */
  private _premium = false;
  /**
   * `true` while the pilot is on a surface AND within liftoff range of
   * a Spaceport (BaseKind.Spaceport, serialized kind === 3). Mirrors
   * the `nearSpaceport` local in {@link updateLandPrompt} so the
   * mobile touch overlay can show a LIFTOFF button on the same gating
   * rule the desktop HUD prompt uses — without the overlay having to
   * re-scan the entity list every frame.
   */
  private _nearSpaceport = false;
  /**
   * Closest surface ruin (Phase 2.1) within the investigate radius,
   * or `null` when nothing is in range. Set by {@link updateRuinPrompt}
   * each frame so main.ts can drive the KeyE hold + send the
   * InvestigateRuin request when the timer completes.
   */
  private _nearbyRuinId: string | null = null;
  private ruinPrompt!: HTMLElement;
  private ruinProgressFill!: HTMLElement;
  private weatherBanner!: HTMLElement;
  private _ruinProgress = 0; // 0..1
  /** Set by main.ts whenever a surface is active so the prompt flips to "LIFTOFF". */
  private _onSurface = false;
  /**
   * Name of the surface the player is currently standing on (e.g.
   * "ATLAS PRIME"). `null` while in space. Drives the zone-label
   * override in `updateZone` so the bottom-of-screen text reads the
   * planet name instead of the parent sector's "DEEP VOID" tier
   * while the player is grounded.
   */
  private _activeSurfaceName: string | null = null;

  private damageOverlay!: HTMLElement;
  private _damageFlashTimer = 0;

  private isMobile = false;
  private topRightPanel!: HTMLElement;

  constructor(container: HTMLElement, opts: { isMobile?: boolean } = {}) {
    this.container = container;
    this.isMobile = !!opts.isMobile;
    this.build();
    this.root.style.display = 'none';
  }

  get nearbyStationId(): string | null {
    return this._nearbyStationId;
  }

  get nearbyWarpGateId(): string | null {
    return this._nearbyWarpGateId;
  }

  get nearbyPlanetId(): string | null {
    return this._nearbyPlanetId;
  }

  get nearbyPlanetPremiumLocked(): boolean {
    return this._nearbyPlanetPremiumLocked;
  }

  /** Mirror the account's premium status so the LAND prompt can badge premium-only planets. */
  setPremium(on: boolean): void {
    this._premium = on;
  }

  /**
   * True iff the pilot is on a surface AND inside the liftoff trigger
   * radius of a Spaceport base. Consumed by `main.ts` to drive the
   * mobile touch overlay's LIFTOFF button (the desktop HUD prompt is
   * driven directly by {@link updateLandPrompt}).
   */
  get nearSpaceport(): boolean {
    return this._nearSpaceport;
  }

  get nearbyRuinId(): string | null {
    return this._nearbyRuinId;
  }

  get isOnSurface(): boolean {
    return this._onSurface;
  }

  setOnSurface(on: boolean): void {
    this._onSurface = on;
    // Re-render the prompt label immediately so the player isn't
    // staring at the wrong action key for the rest of the frame.
    if (this.landPrompt) {
      this.refreshLandPromptLabel();
    }
    if (!on) {
      // Force-clear the cached surface name + zone-label cache so the
      // next zone update re-paints the orbital tier instead of leaving
      // the planet name burned in across a liftoff.
      this._activeSurfaceName = null;
      this._lastZoneBaseKey = '';
    }
  }

  /**
   * Set / clear the live surface name that overrides the orbital zone
   * tier in the HUD. Called from main.ts when {@link MessageType.SurfaceData}
   * arrives. Pass `null` to clear (handled implicitly by `setOnSurface(false)`,
   * but exposed here for symmetry).
   */
  setActiveSurfaceName(name: string | null): void {
    if (this._activeSurfaceName === name) return;
    this._activeSurfaceName = name;
    // Bust the zone-label cache so updateZone re-runs with the new
    // override text immediately rather than waiting for the next
    // sector-cross.
    this._lastZoneBaseKey = '';
  }

  get nearbyMerchantId(): string | null {
    return this._nearbyMerchantId;
  }

  show(): void {
    this.root.style.display = '';
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  private css(el: HTMLElement, styles: Partial<CSSStyleDeclaration>) {
    Object.assign(el.style, styles);
  }

  private el(
    tag: string,
    styles: Partial<CSSStyleDeclaration>,
    text?: string,
  ): HTMLElement {
    const e = document.createElement(tag);
    this.css(e, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build() {
    this.root = this.el('div', {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      fontFamily: FONT,
      color: COLORS.primary,
      fontSize: '12px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      zIndex: '10',
      overflow: 'hidden',
    });
    this.container.appendChild(this.root);

    this.buildTopLeft();
    this.buildTopRight();
    this.buildBottomLeft();
    this.buildBottomRight();
    this.buildBottomCenter();
    this.buildReticle();
    this.buildTargetOverlay();
    this.buildDockPrompt();
    this.buildWarpPrompt();
    this.buildTradePrompt();
    this.buildLandPrompt();
    this.buildRuinPrompt();
    this.buildWeatherBanner();
    this.buildDamageOverlay();
  }

  /* ───── top-left: HP / Shield / Energy ───── */
  private buildTopLeft() {
    const panel = this.el('div', {
      position: 'absolute',
      top: '16px',
      left: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      minWidth: 'clamp(140px, 14vw, 240px)',
    });
    this.root.appendChild(panel);

    const makeBar = (label: string, color: string) => {
      const row = this.el('div', {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      });
      const lbl = this.el('span', {
        width: '52px',
        fontSize: '10px',
        textShadow: GLOW,
        flexShrink: '0',
      }, label);
      const track = this.el('div', {
        flex: '1',
        height: '6px',
        background: 'rgba(0,255,65,0.1)',
        border: `1px solid ${COLORS.dim}`,
        position: 'relative',
        overflow: 'hidden',
      });
      const fill = this.el('div', {
        position: 'absolute',
        top: '0',
        left: '0',
        height: '100%',
        width: '100%',
        background: color,
        boxShadow: `0 0 6px ${color}`,
        transition: 'width 0.15s ease, background 0.1s linear, box-shadow 0.1s linear',
      });
      track.appendChild(fill);
      const val = this.el('span', {
        width: '48px',
        textAlign: 'right',
        fontSize: '10px',
        textShadow: GLOW,
        flexShrink: '0',
      }, '100');
      row.append(lbl, track, val);
      panel.appendChild(row);
      return { fill, val, label: lbl };
    };

    const hp = makeBar(t('hud.health'), COLORS.primary);
    this.hpFill = hp.fill;
    this.hpText = hp.val;

    const sh = makeBar(t('hud.shield'), '#00ccff');
    this.shieldFill = sh.fill;
    this.shieldText = sh.val;

    const en = makeBar(t('hud.energy'), COLORS.secondary);
    this.energyFill = en.fill;
    this.energyText = en.val;
    this.energyLabel = en.label;
    this.energyDefaultLabel = t('hud.energy');
  }

  /* ───── top-right: resources, credits, level ───── */
  private buildTopRight() {
    const panel = this.el('div', {
      position: 'absolute',
      top: '16px',
      right: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      alignItems: 'flex-end',
    });
    this.root.appendChild(panel);
    this.topRightPanel = panel;

    const tiers = [
      ResourceTier.ScrapMetal,
      ResourceTier.CrystalShards,
      ResourceTier.PlasmaCore,
      ResourceTier.VoidEssence,
      ResourceTier.DarkMatter,
      ResourceTier.QuantumFragment,
    ];

    const resRow = this.el('div', {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    });
    panel.appendChild(resRow);

    for (const tier of tiers) {
      const info = RESOURCE_INFO[tier];
      const c = `#${info.color.toString(16).padStart(6, '0')}`;
      const item = this.el('div', {
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
      });
      const dot = this.el('span', {
        display: 'inline-block',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: c,
        boxShadow: `0 0 4px ${c}`,
        flexShrink: '0',
      });
      const num = this.el('span', {
        fontSize: '10px',
        textShadow: GLOW,
      }, '0');
      item.append(dot, num);
      resRow.appendChild(item);
      this.resourceEls.set(tier, num);
    }

    const credRow = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '11px',
      textShadow: GLOW,
    });
    const credLabel = this.el('span', {}, `${t('hud.credits')}:`);
    this.creditsEl = this.el('span', { color: COLORS.secondary }, '0');
    credRow.append(credLabel, this.creditsEl);
    panel.appendChild(credRow);

    const lvlRow = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      minWidth: 'clamp(100px, 10vw, 180px)',
    });
    this.levelEl = this.el('span', {
      fontSize: '11px',
      textShadow: GLOW,
    }, `${t('hud.level')} 1`);
    const xpTrack = this.el('div', {
      flex: '1',
      height: '6px',
      background: 'rgba(0,255,65,0.1)',
      border: `1px solid ${COLORS.dim}`,
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '3px',
    });
    this.xpFill = this.el('div', {
      position: 'absolute',
      top: '0',
      left: '0',
      height: '100%',
      width: '0%',
      background: COLORS.primary,
      boxShadow: `0 0 4px ${COLORS.primary}`,
      transition: 'width 0.3s ease, box-shadow 0.3s ease',
      borderRadius: '3px',
    });
    xpTrack.appendChild(this.xpFill);
    this.xpText = this.el('span', {
      fontSize: '10px',
      textShadow: GLOW,
      minWidth: '50px',
      textAlign: 'right',
    }, '0/100');
    lvlRow.append(this.levelEl, xpTrack, this.xpText);
    panel.appendChild(lvlRow);

    this.statsSummaryEl = this.el('div', {
      fontSize: '9px',
      color: COLORS.dim,
      textShadow: GLOW,
      textAlign: 'right',
      paddingRight: '2px',
      marginTop: '-2px',
    }, 'DMG: 5');
    panel.appendChild(this.statsSummaryEl);
  }

  /* ───── bottom-left: radar ───── */
  private buildBottomLeft() {
    // On mobile landscape the bottom-left corner is occupied by the
    // thrust joystick and the top-left column (HP bars + chat) owns
    // the left side. There isn't enough vertical room on a ~430px
    // landscape phone to stack HP + chat + radar + stick without
    // overlap, so the radar moves to the mid-right — it sits below
    // the top-right credits/weapon stack and above the aim joystick,
    // in the vertical gap that would otherwise be unused. On desktop
    // it stays in its original bottom-left corner.
    const panel = this.el('div', {
      position: 'absolute',
      ...(this.isMobile
        ? {
            // Aligned flush to the same right edge as the top-right
            // stats panel (CREDITS / LEVEL), which sits at `right:16px`.
            // No safe-area inset — the stats panel doesn't use one
            // either, so the radar shares that vertical rail instead
            // of being offset further inward on notched phones.
            top: 'calc(128px + env(safe-area-inset-top, 0px))',
            right: '16px',
          }
        : {
            bottom: '16px',
            left: '16px',
          }),
      display: 'flex',
      flexDirection: 'column',
      alignItems: this.isMobile ? 'flex-end' : 'center',
      gap: '2px',
    });
    this.root.appendChild(panel);

    const radarLabel = this.el('span', {
      fontSize: '9px',
      textShadow: GLOW,
      marginBottom: this.isMobile ? '0' : '2px',
      display: this.isMobile ? 'none' : '',
    }, t('hud.radar'));
    panel.appendChild(radarLabel);

    const size = this.isMobile ? '112px' : 'clamp(100px, 12vw, 160px)';
    const wrapper = this.el('div', {
      width: size,
      height: size,
      borderRadius: '50%',
      border: `1px solid ${COLORS.dim}`,
      background: 'rgba(0,16,0,0.7)',
      boxShadow: BOX_GLOW,
      position: 'relative',
      overflow: 'hidden',
    });
    panel.appendChild(wrapper);

    this.radarCanvas = document.createElement('canvas');
    this.radarCanvas.width = 160;
    this.radarCanvas.height = 160;
    this.css(this.radarCanvas, {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
    });
    wrapper.appendChild(this.radarCanvas);
    this.radarCtx = this.radarCanvas.getContext('2d')!;

    this.sectorCoordsEl = this.el('span', {
      fontSize: this.isMobile ? '10px' : '9px',
      textShadow: GLOW,
      color: COLORS.dim,
      marginTop: this.isMobile ? '4px' : '0',
      letterSpacing: this.isMobile ? '1px' : '',
      whiteSpace: 'nowrap',
    }, `${t('hud.sector')}: 0, 0, 0`);
    panel.appendChild(this.sectorCoordsEl);

    if (this.isMobile) {
      this.destSectorEl = this.el('span', {
        fontSize: '10px',
        textShadow: GLOW,
        color: COLORS.primary,
        letterSpacing: '1px',
        whiteSpace: 'nowrap',
      }, '');
      panel.appendChild(this.destSectorEl);
    }

    this.radarCoordsEl = this.el('span', {
      fontSize: '9px',
      textShadow: GLOW,
      color: COLORS.primary,
      display: this.isMobile ? 'none' : '',
    }, 'X:0 Y:0 Z:0');
    panel.appendChild(this.radarCoordsEl);
  }

  /**
   * Mobile-only compact replacement for the framed bottom-right
   * weapons/shield box. Renders a single text line ("WPN NAME 10/50
   * SHD NAME ∞") appended under the top-right stats row so the same
   * information stays on-screen without stealing the bottom-right
   * thumb zone from the aim joystick. All element references that the
   * regular update code writes into are populated the same way as the
   * desktop panel — the cooldown bar is the only thing that doesn't
   * fit the compact line and is dropped (a one-pixel invisible stub is
   * created so setWeaponCooldownFill can still write to it without
   * special-casing).
   */
  private buildCompactWeaponRow(): void {
    // Ship + race badge as the first line of the compact stack so the
    // pilot can see "what am I flying" at a glance even on a phone
    // where the framed bottom-right panel is hidden.
    this.shipInfoEl = this.el('div', {
      fontSize: '10px',
      color: COLORS.primary,
      textShadow: GLOW,
      letterSpacing: '2px',
      whiteSpace: 'nowrap',
      marginTop: '2px',
    }, '—');
    this.topRightPanel.appendChild(this.shipInfoEl);

    const row = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '10px',
      textShadow: GLOW,
      whiteSpace: 'nowrap',
      marginTop: '2px',
    });

    const wLabel = this.el('span', { color: COLORS.dim }, t('hud.weapons') + ':');
    row.appendChild(wLabel);

    this.weaponNameEl = this.el('span', { color: COLORS.primary }, '---');
    row.appendChild(this.weaponNameEl);

    this.weaponAmmoEl = this.el('span', { color: COLORS.secondary }, '∞');
    row.appendChild(this.weaponAmmoEl);

    this.fireModeEl = this.el('span', { color: COLORS.dim, fontSize: '9px' }, 'SINGLE');
    row.appendChild(this.fireModeEl);

    this.topRightPanel.appendChild(row);

    const shieldRow = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '10px',
      textShadow: GLOW,
      whiteSpace: 'nowrap',
    });
    const sLabel = this.el('span', { color: COLORS.dim }, t('hud.shield') + ':');
    shieldRow.appendChild(sLabel);
    this.shieldNameEl = this.el('span', { color: COLORS.primary }, '---');
    shieldRow.appendChild(this.shieldNameEl);
    this.shieldChargesEl = this.el('span', { color: COLORS.secondary }, '∞');
    shieldRow.appendChild(this.shieldChargesEl);
    this.topRightPanel.appendChild(shieldRow);

    // Invisible stub so update code that writes `width: X%` to the
    // cooldown fill doesn't crash on mobile. The framed cooldown bar
    // is the only element from the desktop layout that has no compact
    // analog; weapon ammo is still visible above.
    this.weaponCooldownFill = this.el('div', {
      display: 'none',
    });
  }

  /* ───── bottom-right: weapon info ───── */
  private buildBottomRight() {
    // Mobile path: render a compact text-only weapon/shield row under
    // the top-right credits/level stack so the framed box doesn't
    // occupy the right (aim) joystick's thumb zone. All HUD update
    // code reads through this.weaponNameEl / weaponAmmoEl / etc., so
    // we just parent those same elements to the compact row instead.
    if (this.isMobile) {
      this.buildCompactWeaponRow();
      return;
    }

    const panel = this.el('div', {
      position: 'absolute',
      bottom: '16px',
      right: '16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '4px',
      background: COLORS.bg,
      padding: '8px 12px',
      border: `1px solid ${COLORS.dim}`,
      boxShadow: BOX_GLOW,
    });
    this.root.appendChild(panel);

    // "FIGHTER · TERRAN" header pinned to the top of the weapon panel
    // so the pilot always knows what hull + race they're flying. Sits
    // above the weapons section and is separated by a 1px divider so
    // the stack reads SHIP → WEAPON → SHIELD.
    this.shipInfoEl = this.el('span', {
      fontSize: '11px',
      color: COLORS.primary,
      textShadow: GLOW,
      letterSpacing: '2px',
    }, '—');
    panel.appendChild(this.shipInfoEl);
    panel.appendChild(this.el('div', {
      width: '100%',
      height: '1px',
      background: COLORS.dim,
      opacity: '0.6',
      marginBottom: '2px',
    }));

    const wLabel = this.el('span', {
      fontSize: '9px',
      color: COLORS.dim,
      textShadow: GLOW,
    }, t('hud.weapons'));
    panel.appendChild(wLabel);

    this.weaponNameEl = this.el('span', {
      fontSize: '13px',
      textShadow: GLOW,
    }, '---');
    panel.appendChild(this.weaponNameEl);

    const ammoRow = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    });
    this.weaponAmmoEl = this.el('span', { fontSize: '11px', textShadow: GLOW }, '∞');
    ammoRow.appendChild(this.weaponAmmoEl);
    panel.appendChild(ammoRow);

    const cdTrack = this.el('div', {
      width: 'clamp(60px, 8vw, 100px)',
      height: '3px',
      background: 'rgba(0,255,65,0.1)',
      border: `1px solid ${COLORS.dim}`,
      position: 'relative',
      overflow: 'hidden',
    });
    this.weaponCooldownFill = this.el('div', {
      position: 'absolute',
      top: '0',
      left: '0',
      height: '100%',
      width: '0%',
      background: COLORS.secondary,
      transition: 'width 0.05s linear',
    });
    cdTrack.appendChild(this.weaponCooldownFill);
    panel.appendChild(cdTrack);

    this.fireModeEl = this.el('span', {
      fontSize: '9px',
      color: COLORS.dim,
      textShadow: GLOW,
    }, 'SINGLE');
    panel.appendChild(this.fireModeEl);

    const shieldBlock = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      marginTop: '6px',
      paddingTop: '6px',
      borderTop: `1px solid ${COLORS.dim}`,
      alignItems: 'flex-end',
    });
    const sLabel = this.el('span', {
      fontSize: '9px',
      color: COLORS.dim,
      textShadow: GLOW,
    }, t('hud.shield'));
    shieldBlock.appendChild(sLabel);
    this.shieldNameEl = this.el('span', {
      fontSize: '11px',
      textShadow: GLOW,
    }, '---');
    shieldBlock.appendChild(this.shieldNameEl);
    this.shieldChargesEl = this.el('span', {
      fontSize: '10px',
      color: COLORS.secondary,
      textShadow: GLOW,
    }, '∞');
    shieldBlock.appendChild(this.shieldChargesEl);
    panel.appendChild(shieldBlock);
  }

  /* ───── bottom-center: speed, coords, zone ───── */
  private buildBottomCenter() {
    const panel = this.el('div', {
      position: 'absolute',
      bottom: '16px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2px',
    });
    this.root.appendChild(panel);

    this.zoneEl = this.el('span', {
      fontSize: '11px',
      padding: '2px 10px',
      border: `1px solid ${COLORS.dim}`,
      letterSpacing: '2px',
      marginBottom: '4px',
    }, '');
    panel.appendChild(this.zoneEl);

    this.speedEl = this.el('span', {
      fontSize: '14px',
      textShadow: GLOW,
    }, `${t('hud.speed')}: 0`);
    panel.appendChild(this.speedEl);

    this.coordsEl = this.el('span', {
      fontSize: '9px',
      color: COLORS.dim,
    }, '0.0, 0.0, 0.0');
    panel.appendChild(this.coordsEl);
  }

  /* ───── center: targeting reticle ───── */
  private buildReticle() {
    this.reticle = this.el('div', {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '48px',
      height: '48px',
      pointerEvents: 'none',
    });
    this.reticle.innerHTML = `<svg viewBox="0 0 48 48" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
      <line x1="24" y1="4"  x2="24" y2="14" stroke="${COLORS.primary}" stroke-width="1" opacity="0.8"/>
      <line x1="24" y1="34" x2="24" y2="44" stroke="${COLORS.primary}" stroke-width="1" opacity="0.8"/>
      <line x1="4"  y1="24" x2="14" y2="24" stroke="${COLORS.primary}" stroke-width="1" opacity="0.8"/>
      <line x1="34" y1="24" x2="44" y2="24" stroke="${COLORS.primary}" stroke-width="1" opacity="0.8"/>
      <polyline points="8,4 4,4 4,8"     fill="none" stroke="${COLORS.primary}" stroke-width="1.5" opacity="0.6"/>
      <polyline points="40,4 44,4 44,8"   fill="none" stroke="${COLORS.primary}" stroke-width="1.5" opacity="0.6"/>
      <polyline points="4,40 4,44 8,44"   fill="none" stroke="${COLORS.primary}" stroke-width="1.5" opacity="0.6"/>
      <polyline points="44,40 44,44 40,44" fill="none" stroke="${COLORS.primary}" stroke-width="1.5" opacity="0.6"/>
      <circle cx="24" cy="24" r="2" fill="none" stroke="${COLORS.primary}" stroke-width="0.8" opacity="0.5"/>
    </svg>`;
    this.root.appendChild(this.reticle);
  }

  /* ───── top-center: target info popup ───── */
  private buildTargetOverlay() {
    // The touch overlay parks the MENU / MAP / INV pill row at the very
    // top-center of the viewport (top: ~12px + safe-area, height ~28px),
    // so the desktop default `top: 16px` would land the target panel on
    // top of those buttons on a phone. Push it below the bar on mobile;
    // desktop keeps the original tight-to-the-top placement.
    const top = this.isMobile
      ? 'calc(54px + env(safe-area-inset-top, 0px))'
      : '16px';
    this.targetOverlay = this.el('div', {
      position: 'absolute',
      top,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '3px',
      background: COLORS.bgSolid,
      padding: '6px 14px',
      border: `1px solid ${COLORS.primary}`,
      boxShadow: BOX_GLOW,
      minWidth: '160px',
    });
    this.root.appendChild(this.targetOverlay);

    const tgtLabel = this.el('span', {
      fontSize: '8px',
      color: COLORS.dim,
    }, t('hud.target'));
    this.targetOverlay.appendChild(tgtLabel);

    this.targetNameEl = this.el('span', {
      fontSize: '12px',
      textShadow: GLOW,
    }, '');
    this.targetOverlay.appendChild(this.targetNameEl);

    const hpTrack = this.el('div', {
      width: '100%',
      height: '4px',
      background: 'rgba(255,68,68,0.15)',
      border: `1px solid rgba(255,68,68,0.3)`,
      position: 'relative',
      overflow: 'hidden',
    });
    this.targetHpFill = this.el('div', {
      position: 'absolute',
      top: '0',
      left: '0',
      height: '100%',
      width: '100%',
      background: COLORS.danger,
      boxShadow: GLOW_DANGER,
      transition: 'width 0.15s ease',
    });
    hpTrack.appendChild(this.targetHpFill);
    this.targetOverlay.appendChild(hpTrack);

    // Shield bar — sits directly under the HP bar so the player can read
    // hull-vs-shield at a glance. Hidden via display:none in
    // `updateTargetInfo` when the target has no shield, so the panel
    // collapses back to the original HP-only layout for asteroids /
    // stations / non-hostile mooks.
    this.targetShieldTrack = this.el('div', {
      width: '100%',
      height: '3px',
      background: 'rgba(0,180,255,0.15)',
      border: `1px solid rgba(0,180,255,0.3)`,
      position: 'relative',
      overflow: 'hidden',
      display: 'none',
    });
    this.targetShieldFill = this.el('div', {
      position: 'absolute',
      top: '0',
      left: '0',
      height: '100%',
      width: '100%',
      background: '#00b4ff',
      boxShadow: '0 0 6px rgba(0,180,255,0.6), 0 0 12px rgba(0,180,255,0.25)',
      transition: 'width 0.15s ease',
    });
    this.targetShieldTrack.appendChild(this.targetShieldFill);
    this.targetOverlay.appendChild(this.targetShieldTrack);

    this.targetDistEl = this.el('span', {
      fontSize: '9px',
      color: COLORS.dim,
    }, '');
    this.targetOverlay.appendChild(this.targetDistEl);
  }

  /* ───── dock prompt ───── */
  private buildDockPrompt() {
    this.dockPrompt = this.el('div', {
      position: 'absolute',
      bottom: '30%',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      background: COLORS.bgSolid,
      padding: '10px 24px',
      border: `1px solid ${COLORS.friendly}`,
      boxShadow: `0 0 12px rgba(0,255,65,0.3)`,
      fontSize: '14px',
      color: COLORS.friendly,
      textShadow: GLOW,
      letterSpacing: '2px',
      whiteSpace: 'nowrap',
    });
    const keyEl = this.el('span', {
      display: 'inline-block',
      padding: '2px 8px',
      border: `1px solid ${COLORS.friendly}`,
      borderRadius: '3px',
      fontSize: '13px',
      fontWeight: 'bold',
    }, 'F');
    const textEl = this.el('span', {}, t('touch.dock'));
    this.dockPrompt.append(keyEl, textEl);
    this.root.appendChild(this.dockPrompt);
  }

  private buildTradePrompt() {
    const color = '#ffcc44';
    this.tradePrompt = this.el('div', {
      position: 'absolute',
      bottom: 'calc(30% + 108px)',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      background: COLORS.bgSolid,
      padding: '10px 24px',
      border: `1px solid ${color}`,
      boxShadow: `0 0 12px rgba(255,204,68,0.3)`,
      fontSize: '14px',
      color,
      textShadow: `0 0 6px ${color}`,
      letterSpacing: '2px',
      whiteSpace: 'nowrap',
    });
    const keyEl = this.el('span', {
      display: 'inline-block',
      padding: '2px 8px',
      border: `1px solid ${color}`,
      borderRadius: '3px',
      fontSize: '13px',
      fontWeight: 'bold',
    }, 'T');
    const textEl = this.el('span', {}, t('merchant.prompt') || 'HAIL MERCHANT');
    this.tradePrompt.append(keyEl, textEl);
    this.root.appendChild(this.tradePrompt);
  }

  private buildLandPrompt() {
    // Bottom-of-screen prompt that flips between LAND and LIFTOFF
    // depending on whether the pilot is in orbit (and near a planet)
    // or already on a surface. Stylistically matches the warp prompt
    // so the HUD doesn't grow a visually orphan element.
    const color = '#ffaa44';
    this.landPrompt = this.el('div', {
      position: 'absolute',
      bottom: 'calc(30% + 90px)',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      background: COLORS.bgSolid,
      padding: '10px 24px',
      border: `1px solid ${color}`,
      boxShadow: `0 0 12px rgba(255,170,68,0.3)`,
      fontSize: '14px',
      color,
      textShadow: `0 0 6px ${color}`,
      letterSpacing: '2px',
      whiteSpace: 'nowrap',
    });
    const keyEl = this.el('span', {
      display: 'inline-block',
      padding: '2px 8px',
      border: `1px solid ${color}`,
      borderRadius: '3px',
      fontSize: '13px',
      fontWeight: 'bold',
    }, 'P');
    keyEl.dataset.role = 'key';
    const textEl = this.el('span', {}, 'LAND');
    textEl.dataset.role = 'label';
    // Gold "PREMIUM" pill shown when the nearby planet's surface is
    // premium-gated and the pilot is free — pressing P then opens the
    // upgrade modal instead of sending a doomed LandRequest.
    const badgeEl = this.el('span', {
      display: 'none',
      padding: '2px 8px',
      border: '1px solid #ffd700',
      borderRadius: '3px',
      fontSize: '11px',
      fontWeight: 'bold',
      color: '#ffd700',
      textShadow: '0 0 6px rgba(255,215,0,0.6)',
      letterSpacing: '1px',
    }, `🔒 ${t('planet.premiumBadge')}`);
    badgeEl.dataset.role = 'premium-badge';
    this.landPrompt.append(keyEl, textEl, badgeEl);
    this.root.appendChild(this.landPrompt);
  }

  private refreshLandPromptLabel(): void {
    const label = this.landPrompt.querySelector<HTMLElement>('[data-role="label"]');
    if (label) label.textContent = this._onSurface ? 'LIFTOFF' : 'LAND';
    const badge = this.landPrompt.querySelector<HTMLElement>('[data-role="premium-badge"]');
    if (badge) {
      badge.style.display =
        !this._onSurface && this._nearbyPlanetPremiumLocked ? 'inline-block' : 'none';
    }
  }

  /**
   * Bottom-of-screen prompt that appears when a pilot drifts into the
   * radius of a discoverable surface ruin (Phase 2.1). Holding KeyE
   * fills the progress bar; releasing it before the 3 s timer
   * completes cancels the investigate. Mirrors the LAND prompt
   * styling so the HUD stays visually coherent.
   */
  private buildRuinPrompt(): void {
    const color = '#bb88ff';
    this.ruinPrompt = this.el('div', {
      position: 'absolute',
      bottom: 'calc(30% + 130px)',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      background: COLORS.bgSolid,
      padding: '8px 22px',
      border: `1px solid ${color}`,
      boxShadow: `0 0 12px rgba(187,136,255,0.3)`,
      fontSize: '13px',
      color,
      textShadow: `0 0 6px ${color}`,
      letterSpacing: '2px',
      whiteSpace: 'nowrap',
    });
    const row = this.el('div', {
      display: 'flex', alignItems: 'center', gap: '8px',
    });
    const keyEl = this.el('span', {
      display: 'inline-block',
      padding: '2px 8px',
      border: `1px solid ${color}`,
      borderRadius: '3px',
      fontSize: '13px',
      fontWeight: 'bold',
    }, 'E');
    const textEl = this.el('span', {}, 'HOLD TO INVESTIGATE');
    row.append(keyEl, textEl);
    const barWrap = this.el('div', {
      width: '180px', height: '4px',
      background: 'rgba(187,136,255,0.18)',
      border: `1px solid ${color}`,
    });
    this.ruinProgressFill = this.el('div', {
      width: '0%', height: '100%',
      background: color, boxShadow: `0 0 6px ${color}`,
      transition: 'width 60ms linear',
    });
    barWrap.appendChild(this.ruinProgressFill);
    this.ruinPrompt.append(row, barWrap);
    this.root.appendChild(this.ruinPrompt);
  }

  /**
   * Top-center banner for the active surface weather event (Phase 2.2).
   * Hidden by default; {@link showSurfaceWeather} flips it on with the
   * event label + remaining seconds.
   */
  private buildWeatherBanner(): void {
    const color = '#88c8ff';
    this.weatherBanner = this.el('div', {
      position: 'absolute',
      top: '70px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'none',
      padding: '6px 18px',
      background: COLORS.bgSolid,
      border: `1px solid ${color}`,
      boxShadow: `0 0 10px rgba(136,200,255,0.3)`,
      fontSize: '12px',
      color,
      textShadow: `0 0 6px ${color}`,
      letterSpacing: '2px',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    }, '');
    this.root.appendChild(this.weatherBanner);
  }

  /**
   * Scan nearby entities for surface ruins (`_extra.ruin === true`)
   * and mark the closest one within investigate range as the active
   * target. The investigate range matches the ruin's authored
   * radius — fetched from `entity.extra.ruinRadius`. Defaults to
   * 12 u when the field is absent for forward-compat.
   */
  updateRuinPrompt(
    player: { transform: { position: { x: number; y: number; z: number } } } | null,
    entities: Iterable<{ id: string; type: EntityType; extra?: Record<string, unknown>; transform: { position: { x: number; y: number; z: number } } }>,
  ): void {
    this._nearbyRuinId = null;
    if (!player || !this._onSurface) {
      this.ruinPrompt.style.display = 'none';
      return;
    }
    const pp = player.transform.position;
    let closest = Infinity;
    let pick: string | null = null;
    for (const e of entities) {
      const x = e.extra;
      if (!x || x['ruin'] !== true) continue;
      const radius = (x['ruinRadius'] as number | undefined) ?? 12;
      const sp = e.transform.position;
      const dx = sp.x - pp.x;
      const dz = sp.z - pp.z;
      const dist2 = dx * dx + dz * dz;
      if (dist2 > radius * radius) continue;
      if (dist2 < closest) {
        closest = dist2;
        pick = e.id;
      }
    }
    this._nearbyRuinId = pick;
    this.ruinPrompt.style.display = !this.isMobile && pick ? 'flex' : 'none';
    if (!pick && this._ruinProgress !== 0) {
      this.setRuinProgress(0);
    }
  }

  /** Drive the investigate progress bar (0..1). Called from main.ts. */
  setRuinProgress(p: number): void {
    const clamped = Math.max(0, Math.min(1, p));
    this._ruinProgress = clamped;
    if (this.ruinProgressFill) {
      this.ruinProgressFill.style.width = `${Math.round(clamped * 100)}%`;
    }
  }

  /**
   * Display the surface weather banner (Phase 2.2). Called from
   * main.ts whenever a {@link SurfaceWeatherMsg} arrives. Passing
   * `null` / `'clear'` hides the banner.
   */
  showSurfaceWeather(msg: { kind: string; remainingMs: number; biome?: string }): void {
    if (!this.weatherBanner) return;
    if (!msg.kind || msg.kind === 'clear') {
      this.weatherBanner.style.display = 'none';
      return;
    }
    const label = msg.kind.replace(/_/g, ' ').toUpperCase();
    const secs = Math.max(0, Math.ceil(msg.remainingMs / 1000));
    this.weatherBanner.textContent = `${label} — ${secs}s`;
    this.weatherBanner.style.display = 'block';
  }

  /**
   * Update the planet-proximity prompt. While the pilot is in orbit
   * we flash LAND when they're close enough to a planet to enter the
   * surface; while on a surface we always show LIFTOFF (no proximity
   * check — the pilot can take off from anywhere on the map).
   */
  updateLandPrompt(
    player: { transform: { position: { x: number; y: number; z: number } } } | null,
    entities: Iterable<{ id: string; type: EntityType; transform: { position: { x: number; y: number; z: number } }; extra?: Record<string, unknown> }>,
    landDistance: number,
  ): void {
    if (this._onSurface) {
      this._nearbyPlanetId = null;
      this._nearSpaceport = isNearSpaceport(player, entities);
      this.refreshLandPromptLabel();
      // Desktop only — the mobile path renders a dedicated LIFTOFF
      // button in the touch overlay (driven by `this._nearSpaceport`
      // via the public getter) so this floating prompt doesn't
      // overlap the bottom-screen joysticks.
      this.landPrompt.style.display =
        !this.isMobile && this._nearSpaceport ? 'flex' : 'none';
      return;
    }

    this._nearSpaceport = false;
    if (!player) {
      this._nearbyPlanetId = null;
      this._nearbyPlanetPremiumLocked = false;
      this.landPrompt.style.display = 'none';
      return;
    }
    const planet = findClosestLandablePlanet(player, entities, landDistance);
    this._nearbyPlanetId = planet?.id ?? null;
    this._nearbyPlanetPremiumLocked =
      planet?.extra?.['premiumOnly'] === true && !this._premium;
    this.refreshLandPromptLabel();
    // Same rationale as the on-surface branch above: mobile gets the
    // touch-overlay LAND button instead of this floating prompt.
    this.landPrompt.style.display =
      !this.isMobile && this._nearbyPlanetId ? 'flex' : 'none';
  }

  private buildWarpPrompt() {
    const color = '#00ccff';
    this.warpPrompt = this.el('div', {
      position: 'absolute',
      bottom: 'calc(30% + 54px)',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      background: COLORS.bgSolid,
      padding: '10px 24px',
      border: `1px solid ${color}`,
      boxShadow: `0 0 12px rgba(0,204,255,0.3)`,
      fontSize: '14px',
      color,
      textShadow: `0 0 6px ${color}`,
      letterSpacing: '2px',
      whiteSpace: 'nowrap',
    });
    const keyEl = this.el('span', {
      display: 'inline-block',
      padding: '2px 8px',
      border: `1px solid ${color}`,
      borderRadius: '3px',
      fontSize: '13px',
      fontWeight: 'bold',
    }, 'G');
    const textEl = this.el('span', {}, t('warpGate.prompt') || 'OPEN WARP GATE');
    this.warpPrompt.append(keyEl, textEl);
    this.root.appendChild(this.warpPrompt);
  }

  updateWarpGatePrompt(
    player: { transform: { position: { x: number; y: number; z: number } } } | null,
    entities: Iterable<{ id: string; type: EntityType; transform: { position: { x: number; y: number; z: number } } }>,
    maxDistance: number,
  ): void {
    this._nearbyWarpGateId = null;

    if (!player) {
      this.warpPrompt.style.display = 'none';
      return;
    }

    const pp = player.transform.position;
    let closestDist = Infinity;

    for (const e of entities) {
      if (e.type !== EntityType.WarpGate) continue;
      const sp = e.transform.position;
      const dx = sp.x - pp.x;
      const dy = sp.y - pp.y;
      const dz = sp.z - pp.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < maxDistance && dist < closestDist) {
        closestDist = dist;
        this._nearbyWarpGateId = e.id;
      }
    }

    // The touch overlay renders its own WARP button (with no key indicator)
    // when `_nearbyWarpGateId` is set; showing the desktop "[G] OPEN WARP
    // GATE" hint on top of it is redundant clutter.
    this.warpPrompt.style.display =
      !this.isMobile && this._nearbyWarpGateId ? 'flex' : 'none';
  }

  updateDockPrompt(
    player: { transform: { position: { x: number; y: number; z: number } } } | null,
    entities: Iterable<{ id: string; type: EntityType; transform: { position: { x: number; y: number; z: number } } }>,
  ): void {
    const DOCK_RANGE = 100;
    this._nearbyStationId = null;

    if (!player) {
      this.dockPrompt.style.display = 'none';
      return;
    }

    const pp = player.transform.position;
    let closestDist = Infinity;

    for (const e of entities) {
      if (e.type !== EntityType.Station) continue;
      const sp = e.transform.position;
      const dx = sp.x - pp.x;
      const dy = sp.y - pp.y;
      const dz = sp.z - pp.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < DOCK_RANGE && dist < closestDist) {
        closestDist = dist;
        this._nearbyStationId = e.id;
      }
    }

    // Mirror the warp/trade prompt behaviour: on mobile the touch overlay
    // owns the dedicated DOCK button, so the desktop "[F] DOCK" hint would
    // just sit on top of it. Keep `_nearbyStationId` populated either way
    // so main.ts can still toggle the touch button.
    this.dockPrompt.style.display =
      !this.isMobile && this._nearbyStationId ? 'flex' : 'none';
  }

  /**
   * Track the closest merchant NPC within trade range so the HUD can
   * flash a "HAIL (T)" prompt. Takes `ClientEntity`-shaped entries
   * (anything exposing `npcTemplateKey` + `disposition`) plus the
   * player position.
   */
  updateMerchantPrompt(
    player: { transform: { position: { x: number; y: number; z: number } } } | null,
    entities: Iterable<{
      id: string;
      type: EntityType;
      disposition?: string;
      npcTemplateKey?: string;
      transform: { position: { x: number; y: number; z: number } };
    }>,
  ): void {
    // Keep this in sync with WorldManager.MERCHANT_TRADE_RANGE on the server.
    const HAIL_RANGE = 260;
    this._nearbyMerchantId = null;

    if (!player) {
      this.tradePrompt.style.display = 'none';
      return;
    }

    const pp = player.transform.position;
    let closestDist = Infinity;

    for (const e of entities) {
      if (e.type !== EntityType.NPC) continue;
      if (e.disposition === 'hostile') continue;
      const isMerchant = e.npcTemplateKey === 'merchant'
        || e.npcTemplateKey === 'cargo_hauler';
      if (!isMerchant) continue;
      const sp = e.transform.position;
      const dx = sp.x - pp.x;
      const dy = sp.y - pp.y;
      const dz = sp.z - pp.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < HAIL_RANGE && dist < closestDist) {
        closestDist = dist;
        this._nearbyMerchantId = e.id;
      }
    }

    // Same rationale as the dock/warp prompts: the touch overlay shows
    // its own TRADE pill on mobile, so suppress the desktop "[T] HAIL
    // MERCHANT" hint there.
    this.tradePrompt.style.display =
      !this.isMobile && this._nearbyMerchantId ? 'flex' : 'none';
  }

  private buildDamageOverlay() {
    this.damageOverlay = document.createElement('div');
    Object.assign(this.damageOverlay.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      opacity: '0',
      transition: 'opacity 0.08s ease-in',
      background: 'radial-gradient(ellipse at center, transparent 40%, rgba(255,20,20,0.5) 100%)',
      zIndex: '15',
    });
    this.container.appendChild(this.damageOverlay);
  }

  triggerDamageFlash(): void {
    this._damageFlashTimer = 0.35;
    this.damageOverlay.style.opacity = '0.9';
  }

  updateDamageOverlay(dt: number): void {
    if (this._damageFlashTimer > 0) {
      this._damageFlashTimer -= dt;
      if (this._damageFlashTimer <= 0) {
        this._damageFlashTimer = 0;
        this.damageOverlay.style.transition = 'opacity 0.3s ease-out';
        this.damageOverlay.style.opacity = '0';
        setTimeout(() => {
          this.damageOverlay.style.transition = 'opacity 0.08s ease-in';
        }, 300);
      }
    }
  }

  /* ═══════════════ UPDATE ═══════════════ */
  update(gameState: GameState, _inputManager: InputManager) {
    const player = gameState.getPlayerEntity();
    const playerState = gameState.getPlayerState();
    const entitiesMap = gameState.getEntities();
    const entities = Array.from(entitiesMap.values());

    if (player) {
      this.updateBars(player, playerState);
      this.updateRadar(player, entities, playerState, gameState.getNavigationTarget());
      this.updateSpeed(player);
      this.updateCoords(player);
      this.updateWeapon(gameState);
    }

    if (playerState) {
      this.updateResources(playerState);
      this.updateCredits(playerState);
      this.updateLevel(playerState);
      this.updateShipInfo(playerState);
      const sk = sectorKey(playerState.sector ?? { x: 0, y: 0, z: 0 });
      const claim = gameState.getSectorClaim(sk);
      this.updateZone(playerState, player, entities, claim);
    }

  }

  private updateBars(player: any, state: any) {
    // Hull/shield are authoritative from the server — `state.hp` mirrors the
    // latest server broadcast, `player.hp` is the same value on the entity.
    // Using `Math.min` here used to pin the bar to stale damage values and
    // hide station regeneration, so now we just trust the freshest snapshot.
    const hp = Math.max(0, state?.hp ?? player.hp ?? 0);
    const maxHp = player.maxHp ?? state?.maxHp ?? 1;
    const pct = (hp / maxHp) * 100;
    this.hpFill.style.width = `${pct}%`;
    this.hpText.textContent = `${Math.ceil(hp)}/${maxHp}`;
    if (pct < 25) {
      this.hpFill.style.background = COLORS.danger;
    } else if (pct < 50) {
      this.hpFill.style.background = COLORS.secondary;
    } else {
      this.hpFill.style.background = COLORS.primary;
    }

    const sh = Math.max(0, state?.shield ?? player.shield ?? 0);
    const maxSh = player.maxShield ?? state?.maxShield ?? 1;
    this.shieldFill.style.width = `${(sh / maxSh) * 100}%`;
    this.shieldText.textContent = `${Math.ceil(sh)}/${maxSh}`;

    // Energy lives on the mirrored LocalPlayerState (see GameState: the
    // server's `en`/`men` fields are copied onto `playerState`, not onto the
    // entity the way hp/shield are). Reading off `state` keeps the boost bar
    // in sync with server-side regen instead of falling back to 0/1.
    const en = state?.energy ?? 0;
    const maxEn = state?.maxEnergy && state.maxEnergy > 0 ? state.maxEnergy : 1;
    const enPct = Math.max(0, Math.min(1, en / maxEn));
    this.energyFill.style.width = `${enPct * 100}%`;
    // Floor (not ceil) so 0.4 of regen reads as "0/100" instead of jumping
    // to "1/100" the instant the regen branch runs — that flicker made the
    // post-depletion cooling look like boost was still engaging briefly.
    this.energyText.textContent = `${Math.floor(en)}/${maxEn}`;

    // Client-side mirror of the server's lockout machine: arm on
    // depletion, clear once we've regenerated past the same 20%
    // threshold the server uses. Keeps the visual feedback in step
    // with the bar even when the wire `bl` flag's delivery is
    // jittery — and self-heals against any old client bundles that
    // predate the protocol bump.
    if (en <= 0) {
      this.energyCoolingLocal = true;
    } else if (this.energyCoolingLocal && enPct >= 0.2) {
      this.energyCoolingLocal = false;
    }

    const cooling = !!state?.boostLocked || this.energyCoolingLocal;
    if (cooling) {
      this.energyFill.style.background = COLORS.danger;
      this.energyFill.style.boxShadow = `0 0 6px ${COLORS.danger}, 0 0 12px rgba(255,68,68,0.5)`;
      this.energyLabel.style.color = COLORS.danger;
      this.energyLabel.style.textShadow = GLOW_DANGER;
      this.energyLabel.textContent = t('hud.boostCooling');
    } else {
      this.energyFill.style.background = COLORS.secondary;
      this.energyFill.style.boxShadow = `0 0 6px ${COLORS.secondary}`;
      this.energyLabel.style.color = '';
      this.energyLabel.style.textShadow = GLOW;
      this.energyLabel.textContent = this.energyDefaultLabel;
    }
  }

  private updateResources(profile: any) {
    const inv: any[] = profile.inventory ?? [];
    const resourceCounts = new Map<ResourceTier, number>();
    for (const item of inv) {
      const match = (item.itemId as string | undefined)?.match(/^resource_(\d+)$/);
      if (match) {
        const tier = parseInt(match[1], 10) as ResourceTier;
        resourceCounts.set(tier, (resourceCounts.get(tier) ?? 0) + (item.quantity ?? 0));
      }
    }
    for (const [tier, el] of this.resourceEls) {
      el.textContent = String(resourceCounts.get(tier) ?? 0);
    }
  }

  private updateCredits(profile: any) {
    const c = profile.credits ?? 0;
    this.creditsEl.textContent = c.toLocaleString();
  }

  private _prevXp = 0;
  private _xpFlashTimer = 0;

  private updateLevel(profile: any) {
    const lvl = profile.level ?? 1;
    const xp = profile.xp ?? 0;
    const xpReq = profile.xpRequired ?? 100;
    const dmg = profile.damage ?? 5;
    this.levelEl.textContent = `${t('hud.level')} ${lvl}`;
    const pct = (xp / Math.max(xpReq, 1)) * 100;
    this.xpFill.style.width = `${pct}%`;
    this.xpText.textContent = `${xp}/${xpReq}`;
    this.statsSummaryEl.textContent = `DMG: ${dmg}`;

    if (xp !== this._prevXp && this._prevXp !== 0) {
      this._xpFlashTimer = 0.6;
      this.xpFill.style.boxShadow = `0 0 8px ${COLORS.secondary}, 0 0 16px ${COLORS.secondary}`;
    }
    this._prevXp = xp;

    if (this._xpFlashTimer > 0) {
      this._xpFlashTimer -= 1 / 60;
      if (this._xpFlashTimer <= 0) {
        this.xpFill.style.boxShadow = `0 0 4px ${COLORS.primary}`;
      }
    }
  }

  /**
   * Cached "lastShipClass|lastRaceId" tuple so we only repaint the
   * ship-info badge when the player actually swaps hulls / races
   * (mid-session ship change in the hangar, or a server-pushed race
   * fix-up). Avoids burning a `t()` lookup every HUD frame.
   */
  private _lastShipInfoKey = '';

  private updateShipInfo(profile: any): void {
    const sc = profile?.shipClass as ShipClass | null | undefined;
    const rid = profile?.raceId as RaceId | null | undefined;
    const key = `${sc ?? '-'}|${rid ?? '-'}`;
    if (key === this._lastShipInfoKey) return;
    this._lastShipInfoKey = key;
    // Pulled through the i18n key generators so the badge respects the
    // active locale (Spanish: "CAZA · TERRÍCOLA"). RACE_INFO is only
    // touched as a guard against unknown ids — `t()` would otherwise
    // surface the raw key in dev builds.
    const shipName = sc != null ? t(shipNameKey(sc)) : '';
    const raceName =
      rid != null && RACE_INFO[rid] ? t(raceNameKey(rid)) : '';
    if (!shipName && !raceName) {
      this.shipInfoEl.textContent = '—';
      return;
    }
    if (shipName && raceName) {
      this.shipInfoEl.textContent = `${shipName.toUpperCase()} · ${raceName.toUpperCase()}`;
    } else {
      this.shipInfoEl.textContent = (shipName || raceName).toUpperCase();
    }
  }

  private _lastZoneBaseKey = '';
  private _lastZoneOwnershipHint = '';
  private _lastSafeState: 'station' | 'warpgate' | null = null;

  private static readonly ZONE_CONFIG: { name: string; color: string; glow: string }[] = [
    { name: 'SAFE ZONE', color: COLORS.primary, glow: GLOW },
    { name: 'CONTESTED SPACE', color: COLORS.secondary, glow: GLOW_WARN },
    { name: 'WAR ZONE', color: COLORS.danger, glow: GLOW_DANGER },
    { name: 'DEEP VOID', color: '#aa44ff', glow: '0 0 6px #aa44ff, 0 0 12px rgba(170,68,255,0.3)' },
  ];

  private zoneOwnershipHint(claim: SectorClaim | undefined): string {
    if (!claim) return '';
    const race = claim.ownerRace != null ? RACE_INFO[claim.ownerRace] : undefined;
    if (claim.ownerPlayerId) {
      return `${claim.ownerName ?? '?'} · ${race?.name ?? ''}`.trim();
    }
    if (race) return race.name;
    return '';
  }

  private updateZone(profile: any, player: any, entities: any[], claim?: SectorClaim) {
    const sector: Vec3 = profile.sector ?? { x: 0, y: 0, z: 0 };

    // Surface override: when the player is grounded the bottom-of-screen
    // tag should read the planet name (e.g. "ATLAS PRIME · SURFACE"),
    // not the parent sector's deep-space tier label. We still draw the
    // tier color underneath for at-a-glance threat reads.
    //
    // We DO still honour the safe-bubble logic — surface bases
    // (Spaceport / Outpost / Fortress / Refinery) are spawned as
    // `EntityType.Station` entities on the server and the server's
    // `updateSafeZoneFlags` classifies them exactly like orbital
    // stations. So a player parked at the spaceport pad on a planet
    // gets the same passive regen + projectile immunity as a player
    // docked at an orbital station — the HUD just needs to TELL the
    // player they're in the bubble.
    //
    // Pre-fix the surface branch early-returned before the safe-bubble
    // scan ran, so the "SAFE ZONE" badge never appeared on a planet
    // even though the underlying combat protection was active —
    // user-reported "nearby from a station in a planet, the Safe Zone
    // label is not being shown".
    if (this._onSurface && this._activeSurfaceName) {
      let surfaceSafe: 'station' | 'warpgate' | null = null;
      if (player) {
        const px = player.transform?.position?.x ?? 0;
        const py = player.transform?.position?.y ?? 0;
        const pz = player.transform?.position?.z ?? 0;
        const stR2 = STATION_SAFE_RADIUS * STATION_SAFE_RADIUS;
        for (const e of entities) {
          if (e.type !== EntityType.Station) continue;
          const ex = e.transform?.position?.x ?? 0;
          const ey = e.transform?.position?.y ?? 0;
          const ez = e.transform?.position?.z ?? 0;
          const dx = px - ex, dy = py - ey, dz = pz - ez;
          if (dx * dx + dy * dy + dz * dz < stR2) {
            surfaceSafe = 'station';
            break;
          }
        }
      }

      const tierIdx = Math.min(
        getZoneDifficulty(sector),
        HUD.ZONE_CONFIG.length - 1,
      );
      const tier = HUD.ZONE_CONFIG[Math.max(tierIdx, 1)]!;
      const safeZoneCfg = HUD.ZONE_CONFIG[0]!;
      const text = surfaceSafe
        ? `${safeZoneCfg.name} · ${this._activeSurfaceName}`
        : `${this._activeSurfaceName} · SURFACE`;
      const color = surfaceSafe ? safeZoneCfg.color : tier.color;
      const glow = surfaceSafe ? safeZoneCfg.glow : tier.glow;
      // Key the cache off both the planet AND the bubble state so the
      // label flips the instant the player crosses the safe radius.
      const baseKey = `surface:${this._activeSurfaceName}:${surfaceSafe ?? 'none'}`;
      const prevBase = this._lastZoneBaseKey;
      const isInitial = prevBase === '';
      if (baseKey !== prevBase) {
        this._lastZoneBaseKey = baseKey;
        this._lastZoneOwnershipHint = '';
        this.zoneEl.textContent = text;
        this.zoneEl.style.color = color;
        this.zoneEl.style.textShadow = glow;
        this.zoneEl.style.borderColor = color;

        // Mirror the orbital "entering station" notification when the
        // player first crosses into / out of a surface base bubble.
        // Skipped on the initial mount so a fresh landing right next
        // to the spaceport doesn't pop a stale notification.
        if (!isInitial && surfaceSafe && this._lastSafeState !== surfaceSafe) {
          this.showNotification(t('zone.enteringStation'), 'success');
        }
      }
      this._lastSafeState = surfaceSafe;
      return;
    }

    // Check if player is near any station OR warp gate. Warp gates use a
    // smaller safe bubble so the two never visually overlap on the HUD.
    let safeSource: 'station' | 'warpgate' | null = null;
    if (player) {
      const px = player.transform?.position?.x ?? 0;
      const py = player.transform?.position?.y ?? 0;
      const pz = player.transform?.position?.z ?? 0;
      const stR2 = STATION_SAFE_RADIUS * STATION_SAFE_RADIUS;
      const gtR2 = WARP_GATE_SAFE_RADIUS * WARP_GATE_SAFE_RADIUS;
      for (const e of entities) {
        const ex = e.transform?.position?.x ?? 0;
        const ey = e.transform?.position?.y ?? 0;
        const ez = e.transform?.position?.z ?? 0;
        const dx = px - ex, dy = py - ey, dz = pz - ez;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (e.type === EntityType.Station && d2 < stR2) {
          safeSource = 'station';
          break;
        }
        if (e.type === EntityType.WarpGate && d2 < gtR2) {
          // Don't early-out: a station bubble should outrank a gate if
          // the player is inside both, so we keep scanning but remember
          // we saw a gate.
          safeSource = safeSource ?? 'warpgate';
        }
      }
    }

    const difficulty = getZoneDifficulty(sector);
    const baseKey = `${sector.x},${sector.y},${sector.z}:${safeSource ?? 'none'}`;
    const hint = !safeSource ? this.zoneOwnershipHint(claim) : '';
    if (baseKey === this._lastZoneBaseKey && hint === this._lastZoneOwnershipHint) {
      return;
    }
    const prevBase = this._lastZoneBaseKey;
    const isInitial = prevBase === '';
    const regionChanged = baseKey !== prevBase;
    this._lastZoneBaseKey = baseKey;
    this._lastZoneOwnershipHint = hint;

    if (safeSource) {
      const zone = HUD.ZONE_CONFIG[0];
      this.zoneEl.textContent = zone.name;
      this.zoneEl.style.color = zone.color;
      this.zoneEl.style.textShadow = zone.glow;
      this.zoneEl.style.borderColor = zone.color;

      if (!isInitial && this._lastSafeState !== safeSource) {
        this.showNotification(
          safeSource === 'station'
            ? t('zone.enteringStation')
            : t('zone.enteringWarpGate'),
          'success',
        );
      }
    } else {
      const zoneIdx = Math.min(difficulty, HUD.ZONE_CONFIG.length - 1);
      const zone = HUD.ZONE_CONFIG[Math.max(zoneIdx, 1)];
      const own = this.zoneOwnershipHint(claim);
      this.zoneEl.textContent = own ? `${zone.name} — ${own}` : zone.name;
      this.zoneEl.style.color = zone.color;
      this.zoneEl.style.textShadow = zone.glow;
      this.zoneEl.style.borderColor = zone.color;

      const name = sectorName(sector);
      if (!isInitial && regionChanged && this._lastSafeState) {
        this.showNotification(
          `${t('zone.leaving')} — ${name} — ${zone.name}`,
          difficulty <= 1 ? 'warning' : 'danger',
        );
      } else if (!isInitial && regionChanged) {
        const extra = own ? ` — ${own}` : '';
        this.showNotification(
          `${t('zone.enteringSector')} ${sector.x}, ${sector.y}, ${sector.z} — ${name}${extra} — ${zone.name}`,
          difficulty <= 1 ? 'warning' : 'danger',
        );
      }
    }

    this._lastSafeState = safeSource;
  }

  private updateRadar(
    player: any,
    entities: any[],
    playerState: any,
    navigationTarget: { x: number; y: number; z: number } | null,
  ) {
    const ctx = this.radarCtx;
    const w = this.radarCanvas.width;
    const h = this.radarCanvas.height;
    const cx = w / 2;
    const cy = h / 2;
    const radius = cx - 4;

    ctx.clearRect(0, 0, w, h);

    // grid rings
    ctx.strokeStyle = 'rgba(0,255,65,0.12)';
    ctx.lineWidth = 0.5;
    for (let i = 1; i <= 3; i++) {
      ctx.beginPath();
      ctx.arc(cx, cy, (radius / 3) * i, 0, Math.PI * 2);
      ctx.stroke();
    }
    // crosshair
    ctx.beginPath();
    ctx.moveTo(cx, cy - radius);
    ctx.lineTo(cx, cy + radius);
    ctx.moveTo(cx - radius, cy);
    ctx.lineTo(cx + radius, cy);
    ctx.stroke();

    // center dot (self)
    ctx.fillStyle = COLORS.primary;
    ctx.beginPath();
    ctx.arc(cx, cy, 2, 0, Math.PI * 2);
    ctx.fill();

    const pos: Vec3 = player.transform?.position ?? { x: 0, y: 0, z: 0 };
    const rot: Vec3 = player.transform?.rotation ?? { x: 0, y: 0, z: 0 };
    const radarRange = 2000;

    // Player's local axes in world space, matching the camera's 'YXZ' Euler
    // order (see main.ts camera update). The radar is a projection onto the
    // player's view plane spanned by (right, forward); the out-of-plane
    // component along local up controls opacity, so objects "above" or
    // "below" the direction you're looking at fade out smoothly.
    const cosY = Math.cos(rot.y);
    const sinY = Math.sin(rot.y);
    const cosX = Math.cos(rot.x);
    const sinX = Math.sin(rot.x);
    const fX = -sinY * cosX, fY = sinX,  fZ = -cosY * cosX; // forward
    const rX = cosY,         rY = 0,     rZ = -sinY;        // right
    const uX = sinY * sinX,  uY = cosX,  uZ = cosY * sinX;  // up

    // ────── compass dial ──────
    // The radar is player-relative (forward = top), so the world's
    // cardinal directions rotate around the rim as the player turns.
    // We project each world cardinal vector onto the player's
    // (right, forward) plane and place a tick + letter at the
    // corresponding bearing. North (-Z by convention, matching the
    // universe map) gets an amber highlight so it anchors the dial;
    // E / S / W use a subtle green so the compass never overpowers
    // the blip cloud or the heading triangle drawn afterwards.
    const compassRim = radius - 3;
    const tickInner = radius - 9;
    const labelInset = radius - 16;
    const cardinals: ReadonlyArray<readonly [number, number, string, boolean]> = [
      [0, -1, 'N', true],
      [1, 0, 'E', false],
      [0, 1, 'S', false],
      [-1, 0, 'W', false],
    ];
    ctx.save();
    ctx.font = `bold 9px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const [wx, wz, label, isNorth] of cardinals) {
      const lRight = wx * rX + wz * rZ;
      const lForward = wx * fX + wz * fZ;
      const lUp = wx * uX + wz * uZ;
      const horiz = Math.sqrt(lRight * lRight + lForward * lForward);
      if (horiz < 1e-3) continue;
      const nx = lRight / horiz;
      const ny = -lForward / horiz;
      // Pitch fade: when the pilot looks straight up / down, the
      // horizontal world plane projects to a tiny stripe across the
      // radar centre; dimming the dial in that regime stops the
      // letters from spinning on top of the heading triangle while
      // still keeping a faint orientation cue.
      const totalMag = Math.sqrt(horiz * horiz + lUp * lUp);
      const alignment = totalMag > 1e-3 ? horiz / totalMag : 1;
      const alpha = Math.max(0.25, alignment * alignment);
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = isNorth ? COLORS.secondary : 'rgba(0,255,65,0.65)';
      ctx.fillStyle = isNorth ? COLORS.secondary : 'rgba(0,255,65,0.8)';
      ctx.shadowColor = isNorth ? COLORS.secondary : 'transparent';
      ctx.shadowBlur = isNorth ? 4 : 0;
      ctx.lineWidth = isNorth ? 2 : 1;
      ctx.beginPath();
      ctx.moveTo(cx + nx * compassRim, cy + ny * compassRim);
      ctx.lineTo(cx + nx * tickInner, cy + ny * tickInner);
      ctx.stroke();
      ctx.fillText(label, cx + nx * labelInset, cy + ny * labelInset);
    }
    ctx.restore();
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    ctx.lineWidth = 1;

    // heading indicator (triangle pointing up = forward)
    ctx.fillStyle = COLORS.primary;
    ctx.beginPath();
    ctx.moveTo(cx, cy - radius + 1);
    ctx.lineTo(cx - 4, cy - radius + 7);
    ctx.lineTo(cx + 4, cy - radius + 7);
    ctx.closePath();
    ctx.fill();

    // Numeric heading from world North (-Z), clockwise from above.
    // 000 = N, 090 = E, 180 = S, 270 = W. Pitch is intentionally
    // ignored so the readout stays stable when the pilot tilts up
    // or down — heading is a horizontal-plane concept.
    const headingRad = Math.atan2(fX, -fZ);
    const heading = ((headingRad * 180) / Math.PI + 360) % 360;
    ctx.save();
    ctx.fillStyle = COLORS.secondary;
    ctx.shadowColor = COLORS.secondary;
    ctx.shadowBlur = 4;
    ctx.font = `bold 10px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(
      `${Math.round(heading).toString().padStart(3, '0')}°`,
      cx,
      cy + radius - 1,
    );
    ctx.restore();

    const LARGE_ENTITY_TYPES = new Set([
      EntityType.Planet, EntityType.Star, EntityType.Station, EntityType.WarpGate,
    ]);

    const blips: RadarBlip[] = [];
    for (const e of entities) {
      if (e === player) continue;
      if ((e.type as EntityType) === EntityType.Projectile) continue;
      const ep: Vec3 = e.transform?.position ?? { x: 0, y: 0, z: 0 };
      const dx = ep.x - pos.x;
      const dy = ep.y - pos.y;
      const dz = ep.z - pos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const isLarge = LARGE_ENTITY_TYPES.has(e.type as EntityType);
      if (dist > radarRange && !isLarge) continue;
      if (dist > radarRange * 2) continue;

      const lRight   = dx * rX + dy * rY + dz * rZ;
      const lForward = dx * fX + dy * fY + dz * fZ;
      const lUp      = dx * uX + dy * uY + dz * uZ;

      // Forward maps to the top of the radar (screen-space y decreases up).
      let rx = (lRight / radarRange) * radius + cx;
      let ry = (-lForward / radarRange) * radius + cy;

      // Clamp distant blips to radar edge
      const bDx = rx - cx;
      const bDz = ry - cy;
      const bDist = Math.sqrt(bDx * bDx + bDz * bDz);
      if (bDist > radius - 3) {
        const scale = (radius - 3) / bDist;
        rx = cx + bDx * scale;
        ry = cy + bDz * scale;
      }

      // Opacity = cos(elevation angle from view plane), floored so blips
      // never fully disappear. ~1 when object lies in the plane you're
      // looking at, 0 when it's directly above/below your view.
      const horizontal = Math.sqrt(lRight * lRight + lForward * lForward);
      const totalMag = Math.sqrt(horizontal * horizontal + lUp * lUp);
      const alignment = totalMag > 0.001 ? horizontal / totalMag : 1;
      const opacity = Math.max(0.15, alignment * alignment);

      let color: string = COLORS.object;
      let blipSize = isLarge ? 3 : 2;
      let shape: RadarBlipShape = 'dot';
      const etype = e.type as EntityType;
      if (etype === EntityType.Ship || etype === EntityType.NPC) {
        const disp = (e as any).disposition;
        if (disp === 'hostile' || disp === 0) color = COLORS.hostile;
        else if (disp === 'friendly' || disp === 2 || disp === 3) color = COLORS.friendly;
        else color = COLORS.neutral;
      } else if (etype === EntityType.Station) {
        // Cyan square keeps stations from bleeding into the green radar
        // grid, and the shape matches their in-world label icon (▣).
        color = COLORS.station;
        blipSize = 4;
        shape = 'square';
      } else if (etype === EntityType.WarpGate) {
        // Purple ring sets warp gates apart from friendlies (green) and
        // stations (cyan) at a glance, mirroring their label icon (◎).
        color = COLORS.warpGate;
        blipSize = 4;
        shape = 'ring';
      } else if (etype === EntityType.Planet || etype === EntityType.Star) {
        color = '#6688cc';
      } else if (etype === EntityType.Asteroid || etype === EntityType.ResourceDrop) {
        color = COLORS.neutral;
      }

      blips.push({ x: rx, y: ry, color, size: blipSize, opacity, shape });
    }

    for (const b of blips) {
      ctx.globalAlpha = b.opacity;
      ctx.fillStyle = b.color;
      ctx.strokeStyle = b.color;
      ctx.shadowColor = b.color;
      ctx.shadowBlur = 4 * b.opacity;
      if (b.shape === 'square') {
        ctx.fillRect(b.x - b.size, b.y - b.size, b.size * 2, b.size * 2);
      } else if (b.shape === 'ring') {
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.size, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.size * 0.35, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    ctx.lineWidth = 1;

    const sc = playerState?.sector ?? { x: 0, y: 0, z: 0 };

    // Destination marker: cast a ray from the player's local position along
    // their forward axis and find where it exits the current sector cube.
    // This tells the pilot which neighboring sector they're flying into and
    // roughly how far away that boundary is.
    const HALF_SECTOR = SECTOR_SIZE / 2;
    const localX = pos.x - sc.x * SECTOR_SIZE;
    const localY = pos.y - sc.y * SECTOR_SIZE;
    const localZ = pos.z - sc.z * SECTOR_SIZE;

    let destT = Infinity;
    let destStepX = 0, destStepY = 0, destStepZ = 0;
    const fAxes: Array<[number, number, number, number, number]> = [
      [fX, localX, 1, 0, 0],
      [fY, localY, 0, 1, 0],
      [fZ, localZ, 0, 0, 1],
    ];
    for (const [dir, p, ax, ay, az] of fAxes) {
      if (Math.abs(dir) < 1e-5) continue;
      const target = dir > 0 ? HALF_SECTOR : -HALF_SECTOR;
      const t = (target - p) / dir;
      if (t > 0 && t < destT) {
        destT = t;
        const sgn = dir > 0 ? 1 : -1;
        destStepX = ax * sgn;
        destStepY = ay * sgn;
        destStepZ = az * sgn;
      }
    }

    if (destT < Infinity && destT > 0) {
      // The ray is along forward, so the projection on the radar lies on the
      // vertical crosshair (lRight ≈ 0). Clamp a bit inside the edge so the
      // marker never collides with the heading triangle at the top.
      const displayT = Math.min(destT, radarRange);
      const markerY = cy - (displayT / radarRange) * radius * 0.85;
      const destColor = COLORS.secondary;
      const faded = destT > radarRange;

      ctx.save();
      ctx.globalAlpha = faded ? 0.55 : 0.9;
      ctx.fillStyle = destColor;
      ctx.strokeStyle = destColor;
      ctx.shadowColor = destColor;
      ctx.shadowBlur = 5;

      // Hollow chevron pointing outward (up) at the boundary crossing point.
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx - 5, markerY + 3);
      ctx.lineTo(cx, markerY - 4);
      ctx.lineTo(cx + 5, markerY + 3);
      ctx.stroke();

      ctx.shadowBlur = 0;
      ctx.globalAlpha = faded ? 0.7 : 1;
      ctx.font = `9px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      const dsx = sc.x + destStepX;
      const dsy = sc.y + destStepY;
      const dsz = sc.z + destStepZ;
      ctx.fillText(`${dsx},${dsy},${dsz}`, cx, markerY - 6);
      ctx.restore();

      if (this.isMobile && this.destSectorEl) {
        // Mobile stacks the two sectors on separate lines because the
        // radar rail on a 430px-wide landscape phone isn't wide enough
        // for the single-line "current → next" formatting.
        this.sectorCoordsEl.textContent =
          `${t('hud.sector')}: ${sc.x}, ${sc.y}, ${sc.z}`;
        this.destSectorEl.textContent = `\u2192 ${dsx}, ${dsy}, ${dsz}`;
      } else {
        this.sectorCoordsEl.textContent =
          `${t('hud.sector')}: ${sc.x}, ${sc.y}, ${sc.z} \u2192 ${dsx}, ${dsy}, ${dsz}`;
      }
    } else {
      this.sectorCoordsEl.textContent = `${t('hud.sector')}: ${sc.x}, ${sc.y}, ${sc.z}`;
      if (this.isMobile && this.destSectorEl) this.destSectorEl.textContent = '';
    }

    // Player-set navigation waypoint (from the Universe Map / M screen).
    // The marker points at the centre of the chosen sector in world
    // coordinates; when out of radar range it sticks to the rim so the
    // pilot always gets a heading cue regardless of distance.
    //
    // The marker *shape* now also encodes the Y-layer delta between
    // the player's current sector and the destination — a flat 2D
    // radar can't otherwise communicate vertical separation, which is
    // a major cause of "I'm right on the marker but the destination
    // doesn't tick down" confusion in 3D space:
    //   - same Y layer → ring/circle
    //   - destination above → up-arrow (climb)
    //   - destination below → down-arrow (dive)
    if (
      navigationTarget &&
      !(
        navigationTarget.x === sc.x &&
        navigationTarget.y === sc.y &&
        navigationTarget.z === sc.z
      )
    ) {
      const tx = navigationTarget.x * SECTOR_SIZE;
      const ty = navigationTarget.y * SECTOR_SIZE;
      const tz = navigationTarget.z * SECTOR_SIZE;
      const wdx = tx - pos.x;
      const wdy = ty - pos.y;
      const wdz = tz - pos.z;
      const wDist = Math.sqrt(wdx * wdx + wdy * wdy + wdz * wdz);
      const wRight = wdx * rX + wdy * rY + wdz * rZ;
      const wForward = wdx * fX + wdy * fY + wdz * fZ;
      // Plane offset controls fade/opacity the same way it does for
      // regular blips, so a waypoint "above" the pilot's view direction
      // looks deprioritised without disappearing.
      const wUp = wdx * uX + wdy * uY + wdz * uZ;

      let wx = (wRight / radarRange) * radius + cx;
      let wy = (-wForward / radarRange) * radius + cy;

      const bDx = wx - cx;
      const bDy = wy - cy;
      const bDist = Math.sqrt(bDx * bDx + bDy * bDy);
      const rim = radius - 4;
      const pinnedToRim = bDist > rim || wDist > radarRange;
      if (bDist > rim) {
        const scale = rim / Math.max(bDist, 0.001);
        wx = cx + bDx * scale;
        wy = cy + bDy * scale;
      }

      const horiz = Math.sqrt(wRight * wRight + wForward * wForward);
      const totalMag = Math.sqrt(horiz * horiz + wUp * wUp);
      const alignment = totalMag > 0.001 ? horiz / totalMag : 1;
      const opacity = Math.max(0.35, alignment * alignment);

      const waypointColor = '#ff66cc';
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.fillStyle = waypointColor;
      ctx.strokeStyle = waypointColor;
      ctx.shadowColor = waypointColor;
      ctx.shadowBlur = 8;
      ctx.lineWidth = 1.8;

      const dy = navigationTarget.y - sc.y;
      // Pinned markers use a slightly larger glyph so they stay
      // legible against the rim ring; in-range glyphs are a touch
      // smaller so they don't visually overpower the radar field.
      const size = pinnedToRim ? 7 : 5.5;

      if (dy === 0) {
        // Same Y layer — ring with center dot. Reads as "fly straight
        // toward this bearing, no climb / dive needed".
        ctx.beginPath();
        ctx.arc(wx, wy, size, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(wx, wy, 1.5, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Different Y layer — solid triangle. Pointed up means "climb
        // (positive Y in world space is up here)", pointed down means
        // "dive". A short stem under the triangle anchors it visually
        // to the bearing point so the pilot reads tip-direction first.
        const dir = dy > 0 ? -1 : 1;
        const tipY = wy + dir * size;
        const baseY = wy - dir * size * 0.4;
        ctx.beginPath();
        ctx.moveTo(wx, tipY);
        ctx.lineTo(wx - size * 0.85, baseY);
        ctx.lineTo(wx + size * 0.85, baseY);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(wx, baseY);
        ctx.lineTo(wx, baseY + dir * -size * 0.9);
        ctx.stroke();
      }
      ctx.restore();
    }

    this.radarCoordsEl.textContent =
      `X:${Math.round(pos.x)}  Y:${Math.round(pos.y)}  Z:${Math.round(pos.z)}`;
  }

  private updateSpeed(player: any) {
    const vel: Vec3 = player.transform?.velocity ?? { x: 0, y: 0, z: 0 };
    const speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z);
    this.speedEl.textContent = `${t('hud.speed')}: ${Math.round(speed)}`;
  }

  private updateCoords(player: any) {
    const pos: Vec3 = player.transform?.position ?? { x: 0, y: 0, z: 0 };
    this.coordsEl.textContent =
      `${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)}`;
  }

  private updateWeapon(gameState: GameState) {
    const eq = gameState.getEquipment();
    const weapon = lookupWeapon(eq.equippedWeaponId);
    const shield = lookupShield(eq.equippedShieldId);

    if (weapon) {
      this.weaponNameEl.textContent = weapon.name.toUpperCase();
      if (eq.weaponAmmo < 0) {
        this.weaponAmmoEl.textContent = '∞';
        this.weaponAmmoEl.style.color = COLORS.primary;
      } else {
        // maxAmmo is the single-pack size; after stacking purchases the
        // reserve can exceed it, so widen the denominator to the larger of
        // the two for a sensible progress read-out.
        const denom = Math.max(eq.weaponMaxAmmo, eq.weaponAmmo);
        this.weaponAmmoEl.textContent = `${eq.weaponAmmo} / ${denom}`;
        const pct = denom > 0 ? eq.weaponAmmo / denom : 1;
        this.weaponAmmoEl.style.color =
          pct < 0.15 ? COLORS.danger
          : pct < 0.35 ? COLORS.secondary
          : COLORS.primary;
      }
    }

    if (shield) {
      this.shieldNameEl.textContent = shield.name.toUpperCase();
      if (eq.shieldCharges < 0) {
        this.shieldChargesEl.textContent = '∞';
        this.shieldChargesEl.style.color = COLORS.primary;
      } else {
        const denom = Math.max(eq.shieldMaxCharges, eq.shieldCharges);
        this.shieldChargesEl.textContent = `${eq.shieldCharges} / ${denom}`;
        const pct = denom > 0 ? eq.shieldCharges / denom : 1;
        this.shieldChargesEl.style.color =
          pct < 0.15 ? COLORS.danger
          : pct < 0.35 ? COLORS.secondary
          : COLORS.primary;
      }
    }

    this.weaponCooldownFill.style.width = '0%';
  }

  updateTargetInfo(
    entity: {
      name?: string;
      hp: number;
      maxHp: number;
      shield?: number;
      maxShield?: number;
      type: EntityType;
      disposition?: string;
    } | null,
    distance: number,
  ): void {
    if (!entity) {
      this.targetOverlay.style.display = 'none';
      return;
    }

    this.targetOverlay.style.display = 'flex';

    const typeName = ENTITY_TYPE_NAMES[entity.type] ?? 'UNKNOWN';
    const displayName = entity.name ?? typeName;
    this.targetNameEl.textContent = displayName;

    const hp = entity.hp ?? 0;
    const maxHp = entity.maxHp ?? 1;
    this.targetHpFill.style.width = `${(hp / maxHp) * 100}%`;

    // Show the shield bar only when the target actually has a shield
    // (driven by the server's `maxShield`, which is 0 for unshielded
    // NPCs / stations / asteroids). This keeps the panel slim for
    // throwaway targets and adds a clear blue band only when the player
    // needs to know "I'm chewing through shield, not hull".
    const maxShield = entity.maxShield ?? 0;
    if (maxShield > 0) {
      const shield = Math.max(0, entity.shield ?? 0);
      this.targetShieldFill.style.width = `${Math.min(1, shield / maxShield) * 100}%`;
      this.targetShieldTrack.style.display = 'block';
    } else {
      this.targetShieldTrack.style.display = 'none';
    }

    this.targetDistEl.textContent = `${t('hud.distance')}: ${Math.round(distance)}M`;

    const disp = entity.disposition;
    if (disp === 'hostile') {
      this.targetNameEl.style.color = COLORS.hostile;
    } else if (disp === 'friendly') {
      this.targetNameEl.style.color = COLORS.friendly;
    } else {
      this.targetNameEl.style.color = COLORS.primary;
    }
  }

  /* ═══════════════ INVENTORY ═══════════════ */
  updateInventory(
    inventory: { itemId: string; quantity: number }[],
    credits: number,
  ) {
    const resourceCounts = new Map<ResourceTier, number>();
    for (const item of inventory) {
      const match = item.itemId.match(/^resource_(\d+)$/);
      if (match) {
        const tier = parseInt(match[1], 10) as ResourceTier;
        resourceCounts.set(tier, (resourceCounts.get(tier) ?? 0) + item.quantity);
      }
    }
    for (const [tier, el] of this.resourceEls) {
      el.textContent = String(resourceCounts.get(tier) ?? 0);
    }
    this.creditsEl.textContent = credits.toLocaleString();
  }

  /* ═══════════════ NOTIFICATIONS ═══════════════ */
  /**
   * Big screen-center level-up banner: gold "LEVEL UP" flash, large new
   * level number, and a one-line stat roll-up. Lifetime ~3s with a pop-in
   * scale animation so it reads as a major event without stealing focus.
   */
  showLevelUpBanner(
    newLevel: number,
    stats?: { maxHp?: number; maxShield?: number; damage?: number },
  ) {
    const gold = '#ffdd44';

    const banner = this.el('div', {
      position: 'absolute',
      left: '50%',
      top: '35%',
      transform: 'translate(-50%, -50%) scale(0.6)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      padding: '18px 36px',
      background: 'rgba(32,20,0,0.78)',
      border: `2px solid ${gold}`,
      borderRadius: '4px',
      boxShadow: `0 0 24px ${gold}, inset 0 0 20px rgba(255,221,68,0.25)`,
      pointerEvents: 'none',
      opacity: '0',
      transition: 'opacity 0.25s ease, transform 0.35s cubic-bezier(.2,1.2,.3,1)',
      zIndex: '999',
    });

    banner.appendChild(this.el('div', {
      fontFamily: FONT,
      fontSize: '14px',
      letterSpacing: '6px',
      color: gold,
      textShadow: `0 0 10px ${gold}`,
    }, t('level.upBanner')));

    banner.appendChild(this.el('div', {
      fontFamily: FONT,
      fontSize: '52px',
      fontWeight: 'bold',
      lineHeight: '1',
      color: '#fff5cc',
      textShadow: `0 0 18px ${gold}, 0 0 4px #fff`,
    }, `LV ${newLevel}`));

    if (stats) {
      banner.appendChild(this.el('div', {
        fontFamily: FONT,
        fontSize: '11px',
        letterSpacing: '2px',
        color: gold,
        opacity: '0.85',
      }, `HP ${stats.maxHp ?? '?'}   SH ${stats.maxShield ?? '?'}   DMG ${stats.damage ?? '?'}`));
    }

    this.root.appendChild(banner);
    requestAnimationFrame(() => {
      banner.style.opacity = '1';
      banner.style.transform = 'translate(-50%, -50%) scale(1)';
    });

    setTimeout(() => {
      banner.style.opacity = '0';
      banner.style.transform = 'translate(-50%, -50%) scale(1.08)';
    }, 2300);
    setTimeout(() => {
      banner.remove();
    }, 2700);
  }

  /**
   * Smaller side-notification shown when another pilot in the sector
   * levels up. Distinct from the self banner so it can coexist without
   * obscuring anything important.
   */
  showPeerLevelUp(pilotName: string, newLevel: number) {
    const label = t('level.peerReached', { name: pilotName, level: newLevel });
    this.showNotification(label, 0xffdd44);
  }

  /**
   * Persistent bottom strip for active territory sieges. Pass `null` to hide.
   */
  setSiegeBanner(text: string | null): void {
    if (this.siegeBannerEl) {
      this.siegeBannerEl.remove();
      this.siegeBannerEl = null;
    }
    if (!text) return;
    const bar = this.el('div', {
      position: 'absolute',
      bottom: '72px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '60',
      padding: '8px 20px',
      background: 'rgba(40,0,0,0.85)',
      border: `2px solid ${COLORS.danger}`,
      color: COLORS.danger,
      fontSize: 'clamp(11px, 1.4vw, 14px)',
      letterSpacing: '2px',
      textShadow: `0 0 8px ${COLORS.danger}`,
      pointerEvents: 'none',
      maxWidth: '90vw',
      textAlign: 'center',
    }, text);
    this.root.appendChild(bar);
    this.siegeBannerEl = bar;
  }

  /**
   * Kept as a stable entry point for gameplay code so existing call sites
   * don't have to reach into `Chat` directly. The floating DOM pills that
   * used to render on the right edge of the screen are gone; instead, the
   * text is forwarded to `onNotify` (see `main.ts` → `Chat.addActivity`).
   *
   * `typeOrColor` accepts either a semantic level or a raw `0xRRGGBB` hex
   * (many gameplay events pass a numeric tier / faction color).
   */
  showNotification(
    text: string,
    typeOrColor: 'info' | 'warning' | 'danger' | 'success' | number = 'info',
  ) {
    let c: string;
    if (typeof typeOrColor === 'number') {
      c = '#' + typeOrColor.toString(16).padStart(6, '0');
    } else {
      const colorMap: Record<string, string> = {
        info: COLORS.primary,
        warning: COLORS.secondary,
        danger: COLORS.danger,
        success: COLORS.primary,
      };
      c = colorMap[typeOrColor];
    }
    this.onNotify?.(text, c);
  }

  /* ═══════════════ DISPOSE ═══════════════ */
  dispose() {
    this.root.remove();
  }
}
